export class FelixChessVariationController {
    constructor(options = {}) {
        this.rootEl = options.rootEl || null;
        this.replay = options.replay || null;
        this.variationsByParentPly = new Map();
        this.nextId = 1;
        this.listeners = new Set();
        this.unsubscribeGameLoaded = null;
        this.unsubscribeCleared = null;

        this.bindReplay();
    }

    bindReplay() {
        if (!this.replay || typeof this.replay.on !== 'function') {
            return;
        }

        this.unsubscribeGameLoaded = this.replay.on('gameloaded', () => {
            this.clear();
        });
        this.unsubscribeCleared = this.replay.on('cleared', () => {
            this.clear();
        });
    }

    clear() {
        this.variationsByParentPly.clear();
        this.nextId = 1;
        this.emitChange();
    }

    onChange(handler) {
        if (typeof handler !== 'function') {
            return () => {};
        }
        this.listeners.add(handler);
        return () => {
            this.listeners.delete(handler);
        };
    }

    getVariationsForParentPly(parentPly) {
        const key = this.normalizeParentPly(parentPly);
        if (key === null) {
            return [];
        }

        return this.cloneVariations(this.variationsByParentPly.get(key) || []);
    }

    addVariation(parentPly, firstMove) {
        const key = this.normalizeParentPly(parentPly);
        const move = this.normalizeMove(firstMove);
        if (key === null || !move) {
            return null;
        }

        const variation = {
            id: this.createId(),
            parentPly: key,
            moves: [move],
            currentPly: 1,
            createdAt: new Date().toISOString()
        };

        const bucket = this.variationsByParentPly.get(key) || [];
        bucket.push(variation);
        this.variationsByParentPly.set(key, bucket);

        this.emitChange();
        return this.cloneVariation(variation);
    }

    appendMove(variationId, moveInput) {
        const move = this.normalizeMove(moveInput);
        if (!variationId || !move) {
            return null;
        }

        const variation = this.findVariation(variationId);
        if (!variation) {
            return null;
        }

        variation.moves.push(move);
        variation.currentPly = variation.moves.length;
        this.emitChange();
        return this.cloneVariation(variation);
    }

    getAllVariations() {
        const out = [];
        for (const variations of this.variationsByParentPly.values()) {
            out.push(...this.cloneVariations(variations));
        }
        return out;
    }

    getVariation(variationId) {
        const variation = this.findVariation(variationId);
        return variation ? this.cloneVariation(variation) : null;
    }

    destroy() {
        if (this.unsubscribeGameLoaded) {
            this.unsubscribeGameLoaded();
            this.unsubscribeGameLoaded = null;
        }
        if (this.unsubscribeCleared) {
            this.unsubscribeCleared();
            this.unsubscribeCleared = null;
        }
        this.clear();
        this.listeners.clear();
        this.rootEl = null;
        this.replay = null;
    }

    emitChange() {
        const payload = {
            variations: this.getAllVariations()
        };
        this.listeners.forEach((listener) => {
            try {
                listener(payload);
            } catch (e) {
                console.error('FelixChessVariationController: listener failed', e);
            }
        });
    }

    createId() {
        const id = `variation-${this.nextId}`;
        this.nextId += 1;
        return id;
    }

    findVariation(variationId) {
        const id = String(variationId || '');
        if (!id) {
            return null;
        }

        for (const variations of this.variationsByParentPly.values()) {
            const found = variations.find((variation) => variation.id === id);
            if (found) {
                return found;
            }
        }
        return null;
    }

    normalizeParentPly(parentPly) {
        const value = Number(parentPly);
        if (!Number.isInteger(value) || value < 0) {
            return null;
        }
        return value;
    }

    normalizeMove(move = {}) {
        if (!move || typeof move !== 'object') {
            return null;
        }

        const san = String(move.san || '').trim();
        const from = String(move.from || '').trim();
        const to = String(move.to || '').trim();
        const fenBefore = String(move.fenBefore || '').trim();
        const fenAfter = String(move.fenAfter || '').trim();

        if (!san || !from || !to || !fenBefore || !fenAfter) {
            return null;
        }

        return {
            san,
            uci: String(move.uci || `${from}${to}${move.promotion || ''}`).trim(),
            from,
            to,
            promotion: String(move.promotion || '').trim(),
            fenBefore,
            fenAfter
        };
    }

    cloneVariations(variations = []) {
        return variations.map((variation) => this.cloneVariation(variation));
    }

    cloneVariation(variation = {}) {
        return {
            id: variation.id,
            parentPly: variation.parentPly,
            moves: Array.isArray(variation.moves)
                ? variation.moves.map((move) => ({ ...move }))
                : [],
            currentPly: variation.currentPly,
            createdAt: variation.createdAt
        };
    }
}
